import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import PageMeta from 'components/seo/PageMeta';
import ErrorBoundary from 'components/feedback/ErrorBoundary';
import { ROUTES } from 'config/routes';
import { useStoreAdminContext } from 'context/StoreAdminContext';
import { useTenant } from 'hooks/useTenant';
import { useStorefrontContext } from 'context/StorefrontContext';

const adminLinks = [
  { label: 'Resumen', path: ROUTES.ADMIN },
  { label: 'Apariencia', path: ROUTES.ADMIN_APPEARANCE },
  { label: 'Layout', path: ROUTES.ADMIN_LAYOUT },
  { label: 'Navegación', path: ROUTES.ADMIN_NAVIGATION },
  { label: 'Catálogo', path: ROUTES.ADMIN_CATALOG },
  { label: 'Funcionalidades', path: ROUTES.ADMIN_FEATURES },
];

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { displayName, logout } = useStoreAdminContext();
  const { tenantCode, tenant } = useTenant();
  const { theme: storeTheme } = useStorefrontContext();
  const isLayoutEditor = location.pathname === ROUTES.ADMIN_LAYOUT;

  const tenantLabel = [tenantCode, storeTheme?.siteName || tenant.siteName]
    .filter(Boolean)
    .join(' · ');

  const sidebar = (
    <List component="nav" aria-label="administración tienda">
      {adminLinks.map((link) => (
        <ListItemButton
          key={link.path}
          selected={location.pathname === link.path}
          onClick={() => navigate(link.path)}
        >
          <ListItemText primary={link.label} />
        </ListItemButton>
      ))}
    </List>
  );

  if (isLayoutEditor) {
    return (
      <>
        <PageMeta title="Layout — Página de inicio" />
        <ErrorBoundary variant="page">
          <Outlet />
        </ErrorBoundary>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Administración de tienda" />
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
            Admin tienda
          </Typography>
          {tenantLabel && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={tenantLabel}
              sx={{ mr: 'auto' }}
            />
          )}
          {!tenantLabel && <Box sx={{ flexGrow: 1 }} />}
          {displayName && (
            <Typography variant="body2" color="text.secondary">
              {displayName}
            </Typography>
          )}
          <Button component={RouterLink} to={ROUTES.HOME} color="inherit">
            Ver tienda
          </Button>
          <Button color="inherit" onClick={logout}>
            Salir
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
          {isMobile ? (
            sidebar
          ) : (
            <Drawer
              variant="permanent"
              open
              sx={{ width: 240, flexShrink: 0, '& .MuiDrawer-paper': { position: 'relative', width: 240 } }}
            >
              {sidebar}
            </Drawer>
          )}
          <Box flexGrow={1}>
            <ErrorBoundary variant="page">
              <Outlet />
            </ErrorBoundary>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default AdminLayout;
